import type {Edge, Node, NodeHandle} from "@xyflow/react";
import {Position} from "@xyflow/react";
import {hierarchy, tree as d3tree} from "d3-hierarchy";
import {PART_SEP} from "~/constants";
import {
    findChanges,
    originalPath,
    type TaxonomyChangeRow,
} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";

export const DIFF_NODE_WIDTH = 260;
/** Room for the name, an old name struck through and two change tags (e.g. "moved from …", "merged into …"). */
export const DIFF_NODE_HEIGHT = 100;
/** Sub-categories shown under the changed category; the rest is summarised as "+N more". */
const MAX_CHILDREN = 5;
const ROW_GAP = 14;
/**
 * Room between columns. Right-angled edges run 20px straight out of a card and 20px into the next one,
 * so anything under 40px makes them double back on themselves; 80px matches the value chain board.
 */
const COLUMN_GAP = 80;

export type DiffNodeData = {
    label: string;
    /** Names before a rename (several when categories were merged into it), shown struck through */
    prevLabel?: string;
    level: number;
    /**
     * focus: the changed category (coloured); context: unchanged, for orientation; more: "+N more";
     * original: a category merged into the focus one, where it was before (dashed)
     */
    role: 'focus' | 'context' | 'more' | 'original';
    /** Deleted (itself or an ancestor): faded, dashed */
    removed: boolean;
    type: m_taxonomy.ChangeType;
    note?: string;
};
export type DiffNode = Node<DiffNodeData, 'diff'>;

/** Edge class names the canvas styles: dashed for the previous state, plus a colour for a move. */
export const PREVIOUS_EDGE_CLASS = 'taxonomy-diff-edge-previous';
export const MOVED_EDGE_CLASS = 'taxonomy-diff-edge-moved';

const HANDLES: NodeHandle[] = [
    {type: 'target', position: Position.Left, x: 0, y: DIFF_NODE_HEIGHT / 2},
    {type: 'source', position: Position.Right, x: DIFF_NODE_WIDTH, y: DIFF_NODE_HEIGHT / 2},
];
/** A merged original's edge into the merged category leaves from its left side, like a bracket. */
export const MERGE_HANDLE = 'merge';
const ORIGINAL_HANDLES: NodeHandle[] = [
    ...HANDLES,
    {id: MERGE_HANDLE, type: 'source', position: Position.Left, x: 0, y: DIFF_NODE_HEIGHT / 2},
];

type Entry = {node: m_taxonomy.Tree, parent: m_taxonomy.Tree | null};
type Item = {id: string, data: DiffNodeData, children: Item[]};

/** Every category by id, removed ones included */
function indexAll(root: m_taxonomy.Tree): Map<number, Entry> {
    const map = new Map<number, Entry>();
    const visit = (node: m_taxonomy.Tree, parent: m_taxonomy.Tree | null) => {
        map.set(node.id, {node, parent});
        node.children.forEach(c => visit(c, node));
    };
    visit(root, null);
    return map;
}

/** The category at a path of labels from L1 (live categories first), or undefined. */
function findByPath(root: m_taxonomy.Tree, labels: string[]): m_taxonomy.Tree | undefined {
    let node: m_taxonomy.Tree | undefined = root;
    for (const label of labels) {
        const matches: m_taxonomy.Tree[] = node?.children.filter(c => c.label === label) ?? [];
        node = matches.find(c => !c.removed) ?? matches[0];
        if (!node) return undefined;
    }
    return node;
}

/**
 * Where a move came from, for its card, e.g. `moved from "Packaging"`: the old parent's name as the backend
 * recorded it in the move's path, not looked up in the tree, which may have renamed, merged or deleted it since.
 */
const movedFrom = (path: string[]) => path.length > 1 ? `moved from "${path.at(-2)}"` : 'moved from the top level';

/** Labels from L1 down to the category's parent (the root, which has no parent, is left out) */
function parentLabels(all: Map<number, Entry>, id: number): string[] {
    const labels: string[] = [];
    for (let p = all.get(id)?.parent; p && all.get(p.id)?.parent; p = all.get(p.id)?.parent) labels.unshift(p.label);
    return labels;
}

/**
 * The paths of the categories merged into this one, each down to its old name. The backend doesn't say
 * which change belongs to which merged category, so they are rebuilt: a move gives the old path, a rename
 * with no move of that name the old name in the current place, and the rest of the `sources` count
 * (merged categories with this name and place) the current path. A new (added) category merged in has
 * no source, so it can't be shown. Nor can any of them once the merged category is deleted: the backend
 * then keeps only the deletion, so there are no names to show.
 */
function mergedOriginals(
    all: Map<number, Entry>,
    root: m_taxonomy.Tree,
    node: m_taxonomy.Tree,
    changes: m_taxonomy.Change[],
): {path: string[], moved: boolean}[] {
    const parents = parentLabels(all, node.id);
    const moved = findChanges(changes, 'moved').map(m => originalPath(root, m));
    const movedNames = new Map<string, number>();
    moved.forEach(path => movedNames.set(path.at(-1) ?? '', (movedNames.get(path.at(-1) ?? '') ?? 0) + 1));
    const renamed = findChanges(changes, 'renamed').flatMap(r => {
        const left = movedNames.get(r.original_label) ?? 0;
        if (left > 0) {
            movedNames.set(r.original_label, left - 1);
            return [];
        }
        return [[...parents, r.original_label]];
    });
    if (moved.length + renamed.length === 0) return [];
    const unchanged = Math.max(0, node.sources.length - moved.length - renamed.length);
    return [
        ...moved.map(path => ({path, moved: true})),
        ...renamed.map(path => ({path, moved: false})),
        ...Array.from({length: unchanged}, () => ({path: [...parents, node.label], moved: false})),
    ];
}

/**
 * One picture of a category's changes, before and after combined, laid out as the taxonomy is now — the
 * prototype's change picture, built from what the backend records:
 * - the path from L1 down to the changed category, plus up to MAX_CHILDREN of its sub-categories
   (the taxonomy itself is not drawn: the first level is L1)
 * - deleted categories stay in place, faded, with dashed edges
 * - a renamed category shows its previous names struck through
 * - a moved category is shown where it is now, in the move's colour, and also shows its old parent,
 *   with a dashed edge from where it hung before
 * - a merged category (more than one source) shows each merged category, with a dashed edge into the
 *   merged one, instead of the old names and parents above: one that kept its parent under it, one that
 *   moved as any moved category (next to the merged one, in the move's colour, with its old parent)
 */
export function buildChangeDiffGraph(
    root: m_taxonomy.Tree,
    row: TaxonomyChangeRow,
): {nodes: DiffNode[], edges: Edge[]} {
    const all = indexAll(root);
    const keep = new Set<number>();
    const keepPath = (id: number | undefined) => {
        for (let e = id === undefined ? undefined : all.get(id); e; e = e.parent ? all.get(e.parent.id) : undefined) keep.add(e.node.id);
    };
    const focusId = row.nodeId;
    const originalId = (i: number) => `original-${focusId}-${i}`;
    keepPath(focusId);
    const focusNode = all.get(focusId)?.node;
    const currentParent = all.get(focusId)?.parent ?? root;

    // An old parent that is the current one (or the root, or gone) adds nothing to the picture.
    const shownOldParent = (path: string[]) => {
        const p = findByPath(root, path.slice(0, -1));
        return p && p.id !== currentParent.id && p.id !== root.id ? p : undefined;
    };

    // Merged: each merged category keyed by the parent it is drawn under. One that kept its parent is drawn
    // under it (the current one when that's gone); one that moved is drawn like any moved category: next to
    // the merged one, with its old parent.
    const isMerge = (focusNode?.sources.length ?? 0) > 1;
    const originalsByParent = new Map<number, {path: string[], i: number, oldParent?: m_taxonomy.Tree, moved: boolean}[]>();
    if (isMerge && focusNode) {
        mergedOriginals(all, root, focusNode, row.changes).forEach(({path, moved}, i) => {
            const oldParent = moved ? shownOldParent(path) : undefined;
            const parent = (moved ? undefined : findByPath(root, path.slice(0, -1))) ?? currentParent;
            keepPath(parent.id);
            if (oldParent) keepPath(oldParent.id);
            originalsByParent.set(parent.id, [...(originalsByParent.get(parent.id) ?? []), {path, i, oldParent, moved}]);
        });
    }

    // Otherwise, the old names and old parents. (A merge records a rename and a move per merged category.)
    const prevLabels = isMerge ? [] : [...new Set(findChanges(row.changes, 'renamed').map(r => r.original_label))];
    const oldParents = isMerge ? [] : [...new Map(findChanges(row.changes, 'moved')
        .flatMap(m => shownOldParent(originalPath(root, m)) ?? [])
        .map(p => [p.id, p])).values()];
    oldParents.forEach(p => keepPath(p.id));

    // Sub-categories under the changed one: live ones, or all of them when it was deleted itself.
    const focusChildren = focusNode
        ? focusNode.children.filter(c => focusNode.removed || !c.removed)
        : [];
    const shownChildren = focusChildren.slice(0, MAX_CHILDREN);
    shownChildren.forEach(c => keep.add(c.id));

    // Every change on the category, e.g. "moved from "Packaging" · description changed".
    // A rename shows as the struck-through old name instead; a merge's renames and moves as the merged categories.
    const note = (): string | undefined => {
        const parts = [...new Set(row.changes.flatMap(change => {
            switch (change.type) {
                case 'added': return ['added'];
                case 'removed': return ['deleted'];
                case 'moved': return isMerge ? [] : [movedFrom(originalPath(root, change))];
                case 'updated': return [`${change.field.replace(/_/g, ' ')} changed`];
                default: return [];
            }
        }))];
        if (isMerge && focusNode) parts.unshift(`merged from ${focusNode.sources.length} categories`);
        return parts.length ? parts.join(PART_SEP) : undefined;
    };

    const toItem = (n: m_taxonomy.Tree, level: number, removedAbove: boolean): Item => {
        const removed = removedAbove || n.removed;
        const isFocus = n.id === focusId;
        const children = n.children.filter(c => keep.has(c.id)).map(c => toItem(c, level + 1, removed));
        // Merged categories that hung here: just before the merged one when it's here too, else last.
        const originals: Item[] = (originalsByParent.get(n.id) ?? []).map(({path, i, moved}) => ({
            id: originalId(i),
            data: {
                label: path.at(-1) ?? '',
                level: level + 1,
                role: 'original',
                removed: false,
                type: moved ? 'moved' : 'renamed',
                note: [
                    ...(moved ? [movedFrom(path)] : []),
                    `merged into "${focusNode?.label}"`,
                ].join(PART_SEP),
            },
            children: [],
        }));
        const focusAt = children.findIndex(c => c.id === String(focusId));
        children.splice(focusAt === -1 ? children.length : focusAt, 0, ...originals);
        if (isFocus && focusChildren.length > shownChildren.length) {
            children.push({
                id: `more-${n.id}`,
                data: {label: `+${focusChildren.length - shownChildren.length} more`, level: level + 1, role: 'more', removed, type: row.type},
                children: [],
            });
        }
        return {
            id: String(n.id),
            data: {
                label: n.label,
                prevLabel: isFocus && prevLabels.length ? prevLabels.join(', ') : undefined,
                level,
                role: isFocus ? 'focus' : 'context',
                removed,
                type: row.type,
                note: isFocus ? note() : undefined,
            },
            children,
        };
    };

    // d3 lays out top-down; swap the axes for a left-to-right tree.
    const h = d3tree<Item>().nodeSize([DIFF_NODE_HEIGHT + ROW_GAP, DIFF_NODE_WIDTH + COLUMN_GAP])(
        hierarchy(toItem(root, 0, false), d => d.children),
    );
    // The root only anchors the layout: its card and its edges to the L1s are left out.
    const rootId = String(root.id);
    const nodes: DiffNode[] = h.descendants().filter(d => d.data.id !== rootId).map(d => ({
        id: d.data.id,
        type: 'diff',
        position: {x: d.y, y: d.x},
        width: DIFF_NODE_WIDTH,
        height: DIFF_NODE_HEIGHT,
        handles: d.data.data.role === 'original' ? ORIGINAL_HANDLES : HANDLES,
        data: d.data.data,
    }));
    const edges: Edge[] = h.links().filter(l => l.source.data.id !== rootId).map(l => ({
        id: `${l.source.data.id}-${l.target.data.id}`,
        source: l.source.data.id,
        target: l.target.data.id,
        type: 'smoothstep',
        // Edges into deleted or merged categories belong to the previous state: dashed.
        ...(l.target.data.data.removed || l.target.data.data.role === 'original' ? {className: PREVIOUS_EDGE_CLASS} : {}),
    }));
    // Each merged category into the merged one: a bracket on their left sides.
    nodes.filter(n => n.data.role === 'original').forEach(original => edges.push({
        id: `merge-${original.id}`,
        source: original.id,
        sourceHandle: MERGE_HANDLE,
        target: String(focusId),
        type: 'smoothstep',
        className: `${PREVIOUS_EDGE_CLASS} ${MOVED_EDGE_CLASS}`,
    }));
    // From each old parent to what moved away from it: the category, or a merged category that moved.
    const moves = [
        ...oldParents.map(oldParent => ({oldParent, target: String(focusId)})),
        ...[...originalsByParent.values()].flat()
            .flatMap(({oldParent, i}) => oldParent ? [{oldParent, target: originalId(i)}] : []),
    ];
    moves.forEach(({oldParent, target}) => edges.push({
        id: `prev-${oldParent.id}-${target}`,
        source: String(oldParent.id),
        target,
        // A curve, so it doesn't run on top of the right-angled current edges into the same card.
        type: 'default',
        className: `${PREVIOUS_EDGE_CLASS} ${MOVED_EDGE_CLASS}`,
    }));
    return {nodes, edges};
}
